import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import DemoAccess from "@/pages/DemoAccess";
import ResetPassword from "@/pages/ResetPassword";
import Profile from "@/pages/Profile";
import Invite from "@/pages/Invite";
import Registry from "@/pages/Registry";
import AdminArbitrators from "@/pages/AdminArbitrators";
import AdminOverview from "@/pages/AdminOverview";
import AdminCompliance from "@/pages/AdminCompliance";
import CauseList from "@/pages/CauseList";
import ArbitratorInvite from "@/pages/ArbitratorInvite";
import Dashboard from "@/pages/Dashboard";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Proceedings from "@/pages/Proceedings";
import Calendar from "@/pages/Calendar";
import Documents from "@/pages/Documents";
import Orders from "@/pages/Orders";
import Adjournments from "@/pages/Adjournments";
import Fees from "@/pages/Fees";
import Alerts from "@/pages/Alerts";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="label-caps" data-testid="auth-loading">Verifying session…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "owner" && !["/registry", "/profile", "/admin/arbitrators", "/admin", "/admin/compliance"].includes(window.location.pathname) && !window.location.pathname.startsWith("/cases/"))
    return <Navigate to="/registry" replace />;
  return <Layout>{children}</Layout>;
};

const Public = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === "owner" ? "/registry" : "/dashboard"} replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<Public><Login /></Public>} />
          <Route path="/demo-access" element={<DemoAccess />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route path="/advocate-change/:token" element={<Invite base="/advocate-changes/by-token" />} />
          <Route path="/arbitrator-invite/:token" element={<ArbitratorInvite />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/cases" element={<Protected><Cases /></Protected>} />
          <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />
          <Route path="/proceedings" element={<Protected><Proceedings /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/documents" element={<Protected><Documents /></Protected>} />
          <Route path="/orders" element={<Protected><Orders /></Protected>} />
          <Route path="/adjournments" element={<Protected><Adjournments /></Protected>} />
          <Route path="/fees" element={<Protected><Fees /></Protected>} />
          <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/registry" element={<Protected><Registry /></Protected>} />
          <Route path="/admin/arbitrators" element={<Protected><AdminArbitrators /></Protected>} />
          <Route path="/admin" element={<Protected><AdminOverview /></Protected>} />
          <Route path="/admin/compliance" element={<Protected><AdminCompliance /></Protected>} />
          <Route path="/cause-list" element={<Protected><CauseList /></Protected>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
